import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  BookOpen,
  RefreshCw,
  Search,
  Trash2,
  Loader2,
  FileText,
  FileWarning,
  Clock,
  CheckCircle2,
  AlertCircle,
  Mail,
  ScanLine,
  Cog,
  PartyPopper,
  ChevronRight,
  Hash,
  Sparkles,
  User,
  CalendarClock,
  Lightbulb,
  ListTodo,
  Star,
  RotateCcw,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface LibraryDocument {
  id: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  status: string;
  errorMessage: string | null;
  chunkCount: number;
  aiImportanceScore: number | null;
  aiAnalyzedAt: string | null;
  createdAt: string;
  sourceEmail: string | null;
  mailMessageId: string | null;
  mailReceivedAt: string | null;
  mailSubject: string | null;
}

interface DocumentChunkDoc {
  id: string;
  filename: string | null;
  contentType: string | null;
  status: string;
  chunkCount: number;
  errorMessage?: string | null;
  aiSummary: string | null;
  aiImportanceScore: number | null;
  aiPersonsMentioned: { name: string; role: string | null }[] | null;
  aiDeadlines: { text: string; date: string | null }[] | null;
  aiDecisions: { text: string }[] | null;
  aiExtractedTaskCount: number | null;
  aiAnalyzedAt: string | null;
}

interface DocumentChunk {
  chunkIndex: number;
  text: string;
}

interface SearchHit {
  documentId: string;
  filename: string | null;
  chunkIndex: number;
  text: string;
  score: number;
}

interface DocumentLink {
  entityType: "contact" | "project" | "mail_message";
  entityId: string;
  label: string;
  sub: string | null;
}

interface DocumentTask {
  id: string;
  title: string | null;
  rawText: string | null;
  status: string;
}

interface DocumentAnalysis {
  id: string;
  aiSummary: string | null;
  aiImportanceScore: number | null;
  aiPersonsMentioned: { name: string; role: string | null }[] | null;
  aiDeadlines: { text: string; date: string | null }[] | null;
  aiDecisions: { text: string }[] | null;
  aiExtractedTaskCount: number | null;
  aiAnalyzedAt: string | null;
  links: DocumentLink[];
}

interface LibraryStatus {
  lastSyncAt: string | null;
  counts: { total: number; ready: number; pending: number; error: number };
}

function useLibraryStatus(fastPoll: boolean) {
  return useQuery<LibraryStatus>({
    queryKey: ["library-status"],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/api/library/status`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("fetch_failed");
      return r.json();
    },
    refetchInterval: fastPoll ? 2000 : 30_000,
  });
}

function useDocuments(fastPoll: boolean) {
  return useQuery<{ documents: LibraryDocument[] }>({
    queryKey: ["library-documents"],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/api/library/documents`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("fetch_failed");
      return r.json();
    },
    refetchInterval: (query) => {
      if (fastPoll) return 2000;
      const docs = query.state.data?.documents ?? [];
      const hasProcessing = docs.some(
        (d) => d.status === "pending" || d.status === "processing",
      );
      return hasProcessing ? 3000 : false;
    },
  });
}

function useDocumentChunks(docId: string | null) {
  return useQuery<{ doc: DocumentChunkDoc; chunks: DocumentChunk[] }>({
    queryKey: ["library-chunks", docId],
    queryFn: async () => {
      const r = await fetch(
        `${API_BASE}/api/library/documents/${docId}/chunks`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("fetch_failed");
      return r.json();
    },
    enabled: !!docId,
    staleTime: 30_000,
  });
}

function useDocumentAnalysis(docId: string | null, enabled: boolean) {
  return useQuery<{ analysis: DocumentAnalysis }>({
    queryKey: ["library-analysis", docId],
    queryFn: async () => {
      const r = await fetch(
        `${API_BASE}/api/library/documents/${docId}/analysis`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("fetch_failed");
      return r.json();
    },
    enabled: !!docId && enabled,
    staleTime: 60_000,
  });
}

function useDocumentTasks(docId: string | null, enabled: boolean) {
  return useQuery<{ tasks: DocumentTask[] }>({
    queryKey: ["library-tasks", docId],
    queryFn: async () => {
      const r = await fetch(
        `${API_BASE}/api/library/documents/${docId}/tasks`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("fetch_failed");
      return r.json();
    },
    enabled: !!docId && enabled,
    staleTime: 60_000,
  });
}

function statusIcon(status: string) {
  if (status === "ready")
    return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />;
  if (status === "processing" || status === "pending")
    return <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin" />;
  if (status === "error")
    return <AlertCircle className="w-3.5 h-3.5 text-destructive" />;
  if (status === "unsupported")
    return <FileWarning className="w-3.5 h-3.5 text-muted-foreground" />;
  return <Clock className="w-3.5 h-3.5 text-muted-foreground" />;
}

function statusLabel(status: string) {
  const map: Record<string, string> = {
    ready: "Prêt",
    processing: "Traitement…",
    pending: "En attente",
    error: "Erreur",
    unsupported: "Format non supporté",
  };
  return map[status] ?? status;
}

function formatBytes(b: number | null) {
  if (!b) return "";
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} Ko`;
  return `${(b / 1024 / 1024).toFixed(1)} Mo`;
}

function highlightQuery(text: string, query: string) {
  if (!query.trim()) return text;
  const words = query
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 1)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (words.length === 0) return text;
  const re = new RegExp(`(${words.join("|")})`, "gi");
  const parts = text.split(re);
  return parts.map((p, i) =>
    re.test(p) ? (
      <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">
        {p}
      </mark>
    ) : (
      p
    ),
  );
}

type SyncPhase = "scanning" | "processing" | "done" | "idle";

function getSyncPhase(
  isSyncing: boolean,
  counts: LibraryStatus["counts"] | undefined,
): SyncPhase {
  if (!isSyncing && !counts?.pending) return "idle";
  if (!counts || counts.total === 0) return "scanning";
  if (counts.pending > 0) return "processing";
  return "done";
}

function SyncProgressBanner({
  phase,
  counts,
  onDismiss,
}: {
  phase: SyncPhase;
  counts: LibraryStatus["counts"] | undefined;
  onDismiss: () => void;
}) {
  const total = counts?.total ?? 0;
  const ready = counts?.ready ?? 0;
  const pending = counts?.pending ?? 0;
  const errors = counts?.error ?? 0;
  const done = ready + errors;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const [indetermPct, setIndetermPct] = useState(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (phase !== "scanning") {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
      return;
    }
    let start: number | null = null;
    function tick(ts: number) {
      if (!start) start = ts;
      const elapsed = ts - start;
      setIndetermPct(((elapsed / 3000) % 1) * 100);
      raf.current = requestAnimationFrame(tick);
    }
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, [phase]);

  if (phase === "idle") return null;

  const config = {
    scanning: {
      icon: <ScanLine className="w-4 h-4 text-primary animate-pulse" />,
      label: "Scan des e-mails…",
      sub: "Recherche des pièces jointes dans vos comptes",
      color: "bg-primary/5 border-primary/20",
    },
    processing: {
      icon: <Cog className="w-4 h-4 text-amber-500 animate-spin" />,
      label: `Traitement des documents… (${done} / ${total})`,
      sub: `${ready} prêt${ready > 1 ? "s" : ""} · ${pending} en attente${errors > 0 ? ` · ${errors} erreur${errors > 1 ? "s" : ""}` : ""}`,
      color: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800",
    },
    done: {
      icon: <PartyPopper className="w-4 h-4 text-green-500" />,
      label: `Synchronisation terminée — ${ready} document${ready > 1 ? "s" : ""} prêt${ready > 1 ? "s" : ""}`,
      sub: errors > 0 ? `${errors} fichier${errors > 1 ? "s" : ""} non supporté${errors > 1 ? "s" : ""}` : "Tous les documents sont indexés",
      color: "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800",
    },
  }[phase];

  return (
    <div className={`rounded-2xl border p-4 space-y-3 ${config.color}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-background/60 flex items-center justify-center shrink-0 mt-0.5">
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{config.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{config.sub}</p>
        </div>
        {phase === "done" && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-muted-foreground shrink-0 -mt-1"
            onClick={onDismiss}
          >
            Masquer
          </Button>
        )}
      </div>

      {phase === "scanning" && (
        <div className="space-y-1">
          <Progress value={indetermPct} className="h-1.5" />
        </div>
      )}

      {phase === "processing" && (
        <div className="space-y-1">
          <Progress value={pct} className="h-1.5" />
          <p className="text-xs text-muted-foreground text-right">{pct}%</p>
        </div>
      )}

      {phase === "done" && (
        <Progress value={100} className="h-1.5" />
      )}
    </div>
  );
}

function importanceMeta(score: number): { label: string; color: string } {
  if (score >= 8)
    return {
      label: "Critique",
      color: "bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800",
    };
  if (score >= 6)
    return {
      label: "Important",
      color: "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800",
    };
  if (score >= 4)
    return {
      label: "Moyen",
      color: "bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800",
    };
  return {
    label: "Faible",
    color: "bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800",
  };
}

function ImportanceBadge({ score }: { score: number }) {
  const { label, color } = importanceMeta(score);
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${color}`}
    >
      <Star className="w-3 h-3" />
      {label} ({score}/10)
    </span>
  );
}

function AnalyseTab({
  doc,
  docId,
  links,
  tasks,
  onAnalyzeRequested,
  isAnalyzing,
}: {
  doc: DocumentChunkDoc | undefined;
  docId: string | null;
  links: DocumentLink[];
  tasks: DocumentTask[];
  onAnalyzeRequested: () => void;
  isAnalyzing: boolean;
}) {
  if (!doc) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (doc.status !== "ready") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
        <p className="text-sm font-medium">Traitement en cours…</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          L'analyse IA sera disponible dès la fin de l'indexation.
        </p>
      </div>
    );
  }

  if (!doc.aiAnalyzedAt) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-primary" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-semibold">Analyse IA non encore effectuée</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Lancez l'analyse pour extraire le résumé, les personnes,
            les échéances, les décisions et les tâches potentielles.
          </p>
        </div>
        <Button
          size="sm"
          onClick={onAnalyzeRequested}
          disabled={isAnalyzing}
        >
          {isAnalyzing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          {isAnalyzing ? "Analyse en cours…" : "Analyser ce document"}
        </Button>
      </div>
    );
  }

  const persons = doc.aiPersonsMentioned ?? [];
  const deadlines = doc.aiDeadlines ?? [];
  const decisions = doc.aiDecisions ?? [];

  return (
    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
      {/* Header row */}
      <div className="flex items-center gap-3 flex-wrap">
        {doc.aiImportanceScore !== null && (
          <ImportanceBadge score={doc.aiImportanceScore} />
        )}
        {doc.aiExtractedTaskCount !== null && doc.aiExtractedTaskCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
            <ListTodo className="w-3 h-3" />
            {doc.aiExtractedTaskCount} tâche{doc.aiExtractedTaskCount > 1 ? "s" : ""} extraite{doc.aiExtractedTaskCount > 1 ? "s" : ""}
          </span>
        )}
        <button
          type="button"
          onClick={onAnalyzeRequested}
          disabled={isAnalyzing}
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          title="Relancer l'analyse"
        >
          {isAnalyzing ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RotateCcw className="w-3 h-3" />
          )}
          <span>Relancer</span>
        </button>
      </div>

      {/* Summary */}
      {doc.aiSummary && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Résumé
          </h3>
          <p className="text-sm leading-relaxed text-foreground/90 bg-muted/30 rounded-xl p-4">
            {doc.aiSummary}
          </p>
        </section>
      )}

      {/* Persons */}
      {persons.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" />
            Personnes mentionnées
          </h3>
          <div className="flex flex-wrap gap-2">
            {persons.map((p, i) => (
              <div
                key={i}
                className="inline-flex flex-col rounded-lg border bg-card px-3 py-1.5"
              >
                <span className="text-xs font-medium">{p.name}</span>
                {p.role && (
                  <span className="text-[11px] text-muted-foreground">{p.role}</span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Deadlines */}
      {deadlines.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" />
            Échéances
          </h3>
          <ul className="space-y-1.5">
            {deadlines.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                <span className="flex-1 text-foreground/90">{d.text}</span>
                {d.date && (
                  <Badge variant="secondary" className="text-[11px] shrink-0">
                    {d.date}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Decisions */}
      {decisions.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Lightbulb className="w-3.5 h-3.5" />
            Décisions &amp; engagements
          </h3>
          <ul className="space-y-1.5">
            {decisions.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                <span className="text-foreground/90">{d.text}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Extracted tasks list */}
      {tasks.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <ListTodo className="w-3.5 h-3.5" />
            Tâches extraites ({tasks.length})
          </h3>
          <ul className="space-y-2">
            {tasks.map((t) => (
              <li
                key={t.id}
                className="flex items-start gap-2 rounded-lg border bg-primary/5 px-3 py-2"
              >
                <ListTodo className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground/90 leading-snug">
                    {t.title ?? t.rawText?.split("\n")[1] ?? "Tâche"}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Visible dans votre boîte Capture · source : document
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Fallback if tasks were extracted but inbox items not yet loaded */}
      {tasks.length === 0 && doc.aiExtractedTaskCount !== null && doc.aiExtractedTaskCount > 0 && (
        <section className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 space-y-1">
          <p className="text-xs font-semibold text-primary flex items-center gap-1.5">
            <ListTodo className="w-3.5 h-3.5" />
            {doc.aiExtractedTaskCount} tâche{doc.aiExtractedTaskCount > 1 ? "s" : ""} créée{doc.aiExtractedTaskCount > 1 ? "s" : ""} dans votre boîte de capture
          </p>
          <p className="text-[11px] text-muted-foreground">
            Retrouvez-les dans l'onglet <strong>Capture</strong> avec la source "document".
          </p>
        </section>
      )}

      {/* Detected links */}
      {links.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <ChevronRight className="w-3.5 h-3.5" />
            Liens détectés
          </h3>
          <div className="flex flex-wrap gap-2">
            {links.map((l, i) => {
              const icon =
                l.entityType === "contact" ? (
                  <User className="w-3 h-3 shrink-0" />
                ) : l.entityType === "project" ? (
                  <Hash className="w-3 h-3 shrink-0" />
                ) : (
                  <Mail className="w-3 h-3 shrink-0" />
                );
              const typeLabel =
                l.entityType === "contact"
                  ? "Contact"
                  : l.entityType === "project"
                    ? "Projet"
                    : "E-mail";
              return (
                <div
                  key={i}
                  className="inline-flex flex-col rounded-lg border bg-card px-3 py-1.5 gap-0.5"
                >
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    {icon}
                    {typeLabel}
                  </span>
                  <span className="text-xs font-medium text-foreground/90 truncate max-w-[160px]">
                    {l.label}
                  </span>
                  {l.sub && (
                    <span className="text-[10px] text-muted-foreground truncate max-w-[160px]">
                      {l.sub}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {persons.length === 0 && deadlines.length === 0 && decisions.length === 0 && !doc.aiSummary && links.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Sparkles className="w-8 h-8 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">Aucune donnée structurée extraite</p>
        </div>
      )}
    </div>
  );
}

function DocumentViewer({
  docId,
  open,
  onClose,
}: {
  docId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, refetch } = useDocumentChunks(open ? docId : null);
  const { toast } = useToast();
  const qc = useQueryClient();
  const doc = data?.doc;
  const chunks = data?.chunks ?? [];
  const [tab, setTab] = useState<"preview" | "chunks" | "analyse">("preview");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch enriched analysis (with links) and extracted tasks when analysis is done
  const analysisEnabled = open && !!doc?.aiAnalyzedAt;
  const { data: analysisData, refetch: refetchAnalysis } = useDocumentAnalysis(
    open ? docId : null,
    analysisEnabled,
  );
  const { data: tasksData, refetch: refetchTasks } = useDocumentTasks(
    open ? docId : null,
    analysisEnabled,
  );
  const links = analysisData?.analysis?.links ?? [];
  const tasks = tasksData?.tasks ?? [];

  // Reset to preview tab whenever a new doc is opened
  useEffect(() => { if (open) setTab("preview"); }, [open, docId]);

  // Stop polling when analysis result arrives; also refresh analysis + links + tasks
  useEffect(() => {
    if (isAnalyzing && doc?.aiAnalyzedAt) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      setIsAnalyzing(false);
      void qc.invalidateQueries({ queryKey: ["library-documents"] });
      void qc.invalidateQueries({ queryKey: ["library-analysis", docId] });
      void qc.invalidateQueries({ queryKey: ["library-tasks", docId] });
      void refetchAnalysis();
      void refetchTasks();
    }
  }, [isAnalyzing, doc?.aiAnalyzedAt, qc, docId, refetchAnalysis, refetchTasks]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  async function handleAnalyzeRequest() {
    if (!docId || isAnalyzing) return;
    setIsAnalyzing(true);
    try {
      const r = await fetch(`${API_BASE}/api/library/documents/${docId}/analyze`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("analyze_failed");
      toast({ title: "Analyse IA lancée", description: "Résultat disponible dans quelques secondes." });
      // Poll: refetch every 3 s; the useEffect above will stop polling when done.
      let attempts = 0;
      pollRef.current = setInterval(() => {
        attempts++;
        void refetch();
        if (attempts > 20) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setIsAnalyzing(false);
        }
      }, 3000);
    } catch {
      toast({ title: "Erreur lors de l'analyse", variant: "destructive" });
      setIsAnalyzing(false);
    }
  }

  const isPdf = doc?.contentType === "application/pdf" ||
    doc?.filename?.toLowerCase().endsWith(".pdf");
  const isImage = doc?.contentType?.startsWith("image/");
  const isDocx =
    doc?.contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    doc?.contentType === "application/msword" ||
    doc?.filename?.toLowerCase().match(/\.(docx?|doc)$/) != null;
  const isText = (doc?.contentType?.startsWith("text/") ||
    doc?.filename?.toLowerCase().match(/\.(txt|md|csv|json)$/) != null) && !isDocx;
  const isViewable = isPdf || isImage || isText || isDocx;

  const fileUrl = docId
    ? `${API_BASE}/api/library/documents/${docId}/file`
    : null;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <FileText className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base font-semibold leading-snug truncate pr-8">
                {isLoading ? "Chargement…" : (doc?.filename ?? "Document")}
              </SheetTitle>
              {doc && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {chunks.length} extrait{chunks.length !== 1 ? "s" : ""} indexé{chunks.length !== 1 ? "s" : ""}
                  {doc.contentType && (
                    <span className="ml-2 opacity-60">· {doc.contentType.split("/").pop()}</span>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-3 bg-muted/50 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setTab("preview")}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${tab === "preview" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Aperçu
            </button>
            <button
              type="button"
              onClick={() => setTab("chunks")}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${tab === "chunks" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Extraits ({chunks.length})
            </button>
            <button
              type="button"
              onClick={() => setTab("analyse")}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors flex items-center justify-center gap-1 ${tab === "analyse" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Sparkles className="w-3 h-3" />
              {doc?.aiAnalyzedAt ? "Analyse" : isAnalyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Analyse"}
            </button>
          </div>
        </SheetHeader>

        {/* Preview tab */}
        {tab === "preview" && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : isViewable && fileUrl ? (
              isPdf ? (
                <iframe
                  src={fileUrl}
                  title={doc?.filename ?? "Document"}
                  className="flex-1 w-full border-0"
                  style={{ minHeight: 0 }}
                />
              ) : isImage ? (
                <div className="flex-1 overflow-auto flex items-start justify-center p-4">
                  <img
                    src={fileUrl}
                    alt={doc?.filename ?? ""}
                    className="max-w-full rounded-lg shadow"
                  />
                </div>
              ) : isDocx ? (
                /* Word — le navigateur ne peut pas afficher .docx nativement,
                   on affiche le texte extrait par mammoth sous forme lisible */
                <div className="flex-1 overflow-auto px-6 py-5 space-y-1">
                  {chunks.length > 0 ? (
                    <>
                      <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
                        <ScanLine className="w-3.5 h-3.5 shrink-0" />
                        <span>Texte extrait du document Word (mammoth)</span>
                        {fileUrl && (
                          <a
                            href={fileUrl}
                            download={doc?.filename ?? undefined}
                            className="ml-auto text-primary underline underline-offset-2 shrink-0"
                          >
                            Télécharger
                          </a>
                        )}
                      </div>
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        {chunks.map((c) => (
                          <p key={c.chunkIndex} className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap mb-3">
                            {c.text}
                          </p>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-16">
                      <FileWarning className="w-8 h-8 text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">Texte non encore extrait</p>
                      {fileUrl && (
                        <a href={fileUrl} download={doc?.filename ?? undefined}
                          className="text-xs text-primary underline underline-offset-2">
                          Télécharger le fichier
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* text / csv / json — show raw in a scrollable <pre> */
                <div className="flex-1 overflow-auto p-4">
                  <pre className="text-xs leading-relaxed whitespace-pre-wrap break-words text-foreground/80 font-mono">
                    {chunks.map((c) => c.text).join("\n\n---\n\n")}
                  </pre>
                </div>
              )
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
                <FileWarning className="w-10 h-10 text-muted-foreground/30" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Aperçu non disponible</p>
                  <p className="text-xs text-muted-foreground max-w-xs">
                    Ce format ne peut pas être affiché directement dans le navigateur.
                  </p>
                </div>
                {fileUrl && (
                  <a
                    href={fileUrl}
                    download={doc?.filename ?? undefined}
                    className="inline-flex items-center gap-2 text-xs text-primary underline underline-offset-2"
                  >
                    Télécharger le fichier
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* Chunks tab */}
        {tab === "chunks" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-24 w-full rounded-xl" />
                  <Skeleton className="h-24 w-full rounded-xl" />
                  <Skeleton className="h-24 w-full rounded-xl" />
                </div>
              ) : chunks.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-16 text-center">
                  {doc?.status === "pending" || doc?.status === "processing" ? (
                    <>
                      <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
                      <p className="text-sm font-medium">Traitement en cours…</p>
                      <p className="text-xs text-muted-foreground max-w-xs">
                        Le texte sera disponible dès la fin de l'extraction.
                      </p>
                    </>
                  ) : doc?.status === "error" ? (
                    <>
                      <AlertCircle className="w-8 h-8 text-destructive/50" />
                      <p className="text-sm font-medium">Extraction échouée</p>
                      <p className="text-xs text-muted-foreground max-w-xs">
                        {doc.errorMessage ?? "Ce fichier n'a pas pu être traité."}
                      </p>
                    </>
                  ) : (
                    <>
                      <FileWarning className="w-8 h-8 text-muted-foreground/40" />
                      <p className="text-sm font-medium text-muted-foreground">
                        Aucun texte extrait
                      </p>
                      <p className="text-xs text-muted-foreground max-w-xs">
                        Ce fichier n'a pas pu être indexé (format non supporté ou PDF image sans texte reconnu).
                      </p>
                    </>
                  )}
                </div>
              ) : (
                chunks.map((chunk) => (
                  <div
                    key={chunk.chunkIndex}
                    className="rounded-xl border bg-muted/30 p-4 space-y-2"
                  >
                    <div className="flex items-center gap-1.5">
                      <Hash className="w-3 h-3 text-muted-foreground/60" />
                      <span className="text-[11px] font-mono text-muted-foreground/60 font-medium">
                        Extrait {chunk.chunkIndex + 1}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">
                      {chunk.text}
                    </p>
                  </div>
                ))
              )}
            </div>

            {chunks.length > 0 && (
              <div className="px-6 py-3 border-t shrink-0 bg-muted/20">
                <p className="text-xs text-muted-foreground">
                  Ces extraits sont utilisés par l'IA pour répondre à vos questions (recherche BM25).
                </p>
              </div>
            )}
          </>
        )}

        {/* Analyse tab */}
        {tab === "analyse" && (
          <AnalyseTab
            doc={doc}
            docId={docId}
            links={links}
            tasks={tasks}
            onAnalyzeRequested={handleAnalyzeRequest}
            isAnalyzing={isAnalyzing}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function LibraryPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, navigate] = useLocation();

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const fastPoll = isSyncing || syncDone;
  const { data, isLoading } = useDocuments(fastPoll);
  const { data: statusData } = useLibraryStatus(fastPoll);
  const docs = data?.documents ?? [];

  const counts = statusData?.counts;
  const phase = syncDone && !isSyncing && (counts?.pending ?? 0) === 0
    ? "done"
    : getSyncPhase(isSyncing, counts);

  useEffect(() => {
    if (isSyncing && counts && counts.total > 0 && counts.pending === 0) {
      setIsSyncing(false);
      setSyncDone(true);
    }
  }, [isSyncing, counts]);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    fetch(`${API_BASE}/api/library/search?q=${encodeURIComponent(debouncedQuery)}`, { credentials: "include" })
      .then((r) => r.json() as Promise<{ hits: SearchHit[] }>)
      .then((json) => { if (!cancelled) setSearchResults(json.hits); })
      .catch(() => { if (!cancelled) toast({ title: "Erreur de recherche", variant: "destructive" }); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const syncMut = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_BASE}/api/library/sync`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("sync_failed");
      return r.json() as Promise<{ started: boolean }>;
    },
    onMutate: () => {
      setIsSyncing(true);
      setSyncDone(false);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["library-documents"] });
      qc.invalidateQueries({ queryKey: ["library-status"] });
    },
    onError: () => {
      setIsSyncing(false);
      toast({ title: "Erreur de synchronisation", variant: "destructive" });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`${API_BASE}/api/library/documents/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok && r.status !== 204) throw new Error("delete_failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["library-documents"] });
      if (selectedDocId && viewerOpen) {
        setViewerOpen(false);
        setSelectedDocId(null);
      }
      toast({ title: "Document supprimé" });
    },
    onError: () =>
      toast({ title: "Impossible de supprimer", variant: "destructive" }),
  });

  function openViewer(docId: string) {
    setSelectedDocId(docId);
    setViewerOpen(true);
  }

  const lastSyncAt = statusData?.lastSyncAt ? new Date(statusData.lastSyncAt) : null;
  const isActivelySyncing = isSyncing || (counts?.pending ?? 0) > 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Bibliothèque</h1>
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded self-end mb-1">
              Beta
            </span>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Documents extraits de vos pièces jointes, interrogeables par l'IA.
          </p>
          {lastSyncAt && !isActivelySyncing && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              Dernière synchro{" "}
              {formatDistanceToNow(lastSyncAt, { addSuffix: true, locale: frLocale })}
              {counts && counts.total > 0 && (
                <> · <span className="font-medium text-foreground">{counts.total}</span> document{counts.total > 1 ? "s" : ""}</>
              )}
            </p>
          )}
        </div>
        <Button
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending || isActivelySyncing}
          variant={isActivelySyncing ? "secondary" : "default"}
          className="shrink-0"
        >
          {isActivelySyncing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          {isActivelySyncing ? "En cours…" : "Synchroniser"}
        </Button>
      </div>

      {/* Progress banner */}
      {phase !== "idle" && (
        <SyncProgressBanner
          phase={phase}
          counts={counts}
          onDismiss={() => setSyncDone(false)}
        />
      )}

      {/* Search */}
      <div className="relative">
        {searching ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin pointer-events-none" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        )}
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Chercher dans vos documents… (ex : Pierre, facture, contrat)"
          className="pl-9"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); setSearchResults(null); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Effacer"
          >
            ×
          </button>
        )}
      </div>

      {/* Search results */}
      {searchResults !== null && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground">
            {searchResults.length === 0
              ? "Aucun résultat pour cette recherche."
              : `${searchResults.length} résultat(s)`}
          </p>
          {searchResults.map((hit, i) => (
            <button
              key={i}
              type="button"
              onClick={() => openViewer(hit.documentId)}
              className="w-full text-left rounded-xl border bg-card p-4 space-y-1.5 hover:bg-muted/40 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm font-semibold truncate">
                  {hit.filename ?? "Document"}
                </span>
                <Badge variant="secondary" className="ml-auto text-xs font-mono shrink-0">
                  p.{hit.chunkIndex + 1}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4">
                {highlightQuery(hit.text, query)}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Document list */}
      {searchResults === null && (
        <div className="space-y-2">
          {isLoading ? (
            <>
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-16 w-full rounded-xl" />
            </>
          ) : docs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 px-6 py-10 flex flex-col items-center gap-3 text-center">
              <BookOpen className="w-8 h-8 text-primary/50" />
              <p className="text-sm font-medium text-foreground">Aucun document</p>
              <p className="text-xs text-muted-foreground max-w-xs">
                Cliquez sur "Synchroniser" pour importer les pièces jointes de
                vos mails (PDF, Word, texte).
              </p>
            </div>
          ) : (
            docs.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => doc.status === "ready" ? openViewer(doc.id) : undefined}
                className={`w-full text-left flex items-center gap-3 rounded-xl border bg-card px-4 py-3 group transition-colors ${doc.status === "ready" ? "hover:bg-muted/40 cursor-pointer" : "cursor-default"}`}
              >
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {doc.filename ?? "Sans nom"}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1">
                      {statusIcon(doc.status)}
                      {statusLabel(doc.status)}
                    </span>
                    {doc.status === "ready" && doc.chunkCount > 0 && (
                      <span>· {doc.chunkCount} extrait(s)</span>
                    )}
                    {doc.sizeBytes && (
                      <span>· {formatBytes(doc.sizeBytes)}</span>
                    )}
                    {doc.status === "error" && doc.errorMessage && (
                      <span
                        className="text-destructive truncate max-w-[200px]"
                        title={doc.errorMessage}
                      >
                        · {doc.errorMessage}
                      </span>
                    )}
                  </div>
                  {(doc.sourceEmail || doc.mailReceivedAt) && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70 mt-0.5">
                      {doc.mailMessageId ? (
                        <button
                          type="button"
                          title="Ouvrir l'e-mail"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/app/mail?id=${doc.mailMessageId}`);
                          }}
                          className="flex items-center gap-1 hover:text-primary transition-colors underline-offset-2 hover:underline"
                        >
                          <Mail className="w-3 h-3 shrink-0" />
                          <span className="truncate max-w-[160px]">{doc.sourceEmail}</span>
                        </button>
                      ) : (
                        <>
                          <Mail className="w-3 h-3 shrink-0" />
                          <span className="truncate max-w-[160px]">{doc.sourceEmail}</span>
                        </>
                      )}
                      {doc.mailReceivedAt && (
                        <span
                          className="shrink-0"
                          title={format(new Date(doc.mailReceivedAt), "d MMMM yyyy 'à' HH:mm", { locale: frLocale })}
                        >
                          · {format(new Date(doc.mailReceivedAt), "d MMM yyyy", { locale: frLocale })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {doc.status === "ready" ? (
                  <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0 group-hover:text-muted-foreground transition-colors" />
                ) : (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteMut.mutate(doc.id); }}
                    disabled={deleteMut.isPending}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {/* Document viewer panel */}
      <DocumentViewer
        docId={selectedDocId}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
      />
    </div>
  );
}
